import {
  EGRESS_REASONS,
  type EgressReason,
  FEEDBACK_DRAFT_TOOL_NAME,
  FIND_PEOPLE_TOOL_NAME,
  isToolPart,
  MEMORY_TOOL_NAMES,
  PRODUCT_HELP_TOOL_NAME,
  TASK_TOOL_NAMES,
  TOOL_APPROVALS_PART,
  type ToolApprovalsData,
  toolApprovalsDataSchema,
  toolNameOf,
  toolRefusalSchema,
  WEB_FETCH_ERROR_CODES,
  type WebFetchErrorCode,
} from "@esi/shared";
import { APICallError } from "ai";

/** Knowledge tools that read only inside ESI: their parts get no activity row. */
const INTERNAL_TOOLS = new Set([
  "search_knowledge",
  "document_outline",
  "open_section",
  "propose_wiki_page",
  // The wiki tools (wiki-collaboration): ESI's own wiki, never outbound.
  "wiki_tree",
  "wiki_read",
  "wiki_write",
  "wiki_changes",
  // A thread reply draft (wiki-resolve-with-agent): stored in ESI, sent by the author only.
  "wiki_reply",
  // Conversation attachments (conversation-attachments): read inside ESI, never outbound.
  "search_attachments",
  "attachment_outline",
  "open_attachment_section",
  // Product help (agent-self-knowledge-help): the build's own help corpus, never outbound.
  PRODUCT_HELP_TOOL_NAME,
  // A feedback draft (feedback-from-conversation): stored in ESI, shown as its card.
  FEEDBACK_DRAFT_TOOL_NAME,
  // Task and memory tools (agent-turn D9–D13): read or propose inside ESI, shown as cards.
  ...TASK_TOOL_NAMES,
  ...MEMORY_TOOL_NAMES,
  // People signals (agent-turn D22): read inside ESI, shown in the answer and its people step.
  FIND_PEOPLE_TOOL_NAME,
]);

/** A tool part as the activity row reads it (AI SDK `ToolUIPart`, loosely typed). */
export interface ToolPart {
  type: string;
  toolName?: unknown;
  toolCallId?: unknown;
  state?: unknown;
  input?: unknown;
  output?: unknown;
  errorText?: unknown;
  approval?: { id?: unknown; approved?: unknown } | undefined;
}

const isExternalOutput = (output: unknown) =>
  typeof output === "object" &&
  output !== null &&
  (output as { external?: unknown }).external === true;

/**
 * Whether a part is an outbound tool call (one that sends its arguments outside ESI): its result
 * is external or a refusal, it asked for approval, or it is not one of the internal tools.
 */
export function isOutboundToolPart(part: unknown): part is ToolPart {
  if (!isToolPart(part)) {
    return false;
  }
  const p = part as ToolPart;
  if (isExternalOutput(p.output) || toolRefusalSchema.safeParse(p.output).success) {
    return true;
  }
  if (
    p.state === "approval-requested" ||
    p.state === "approval-responded" ||
    p.state === "output-denied"
  ) {
    return true;
  }
  return !INTERNAL_TOOLS.has(toolNameOf(p));
}

/** The message's latest `data-tool-approvals` part, if any (the server rebuilds it per render). */
export function approvalsOf(parts: readonly unknown[]): ToolApprovalsData | undefined {
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i] as { type?: unknown; data?: unknown };
    if (part?.type === TOOL_APPROVALS_PART) {
      const parsed = toolApprovalsDataSchema.safeParse(part.data);
      return parsed.success ? parsed.data : undefined;
    }
  }
  return undefined;
}

/** The approval ID of a tool part, when it has one. */
export const approvalIdOf = (part: ToolPart): string | undefined =>
  typeof part.approval?.id === "string" ? part.approval.id : undefined;

/** The URL of the decision endpoint for one approval. */
export const toolApprovalPath = (conversationId: string, approvalId: string) =>
  `/api/conversations/${conversationId}/tool-approvals/${encodeURIComponent(approvalId)}`;

/**
 * The decision a chat request carries, when it is one: the assistant message `messageId` names
 * (else the last message) holds a part the user just answered (`approval-responded`) whose
 * decision was not sent yet. A user message never is one, so a stale answered part left after a
 * refused decision never takes over a later question.
 */
export function pendingDecisionOf(
  messages: readonly { id: string; role: string; parts: readonly unknown[] }[],
  messageId: string | undefined,
  sent: ReadonlySet<string>,
): { approvalId: string; decision: "approve" | "deny" } | null {
  const message = messageId ? messages.find((m) => m.id === messageId) : messages.at(-1);
  if (message?.role !== "assistant") {
    return null;
  }
  for (const part of message.parts) {
    if (isToolPart(part) && part.state === "approval-responded") {
      const id = approvalIdOf(part as ToolPart);
      if (id && !sent.has(id)) {
        const approved = (part as ToolPart).approval?.approved === true;
        return { approvalId: id, decision: approved ? "approve" : "deny" };
      }
    }
  }
  return null;
}

/**
 * Why a decision was refused, as the row explains it: `expired` (`approval_expired`), `decided`
 * (`approval_decided`: answered already, e.g. from another tab), `unavailable` (unknown or not
 * the viewer's, `approval_mismatch`, or another conflict such as a reply still running),
 * `budget` (`429 model_budget`: the organisation's hard monthly AI budget is used up; the call
 * stays pending on the server and can be decided again once the budget allows it), `userBudget`
 * (`429 user_budget`: the same for the user's share of that budget).
 */
export type DecisionFailure = "expired" | "decided" | "unavailable" | "budget" | "userBudget";

/**
 * Reads a failed chat request: a refused decision (`404`, `409` or `429 model_budget` from the
 * decision endpoint) names its approval in the URL. Anything else is a failed turn, not a
 * decision failure.
 */
export function decisionFailureOf(
  error: unknown,
): { approvalId: string; failure: DecisionFailure } | null {
  if (!APICallError.isInstance(error)) {
    return null;
  }
  const match = /\/tool-approvals\/([^/?#]+)$/.exec(
    new URL(error.url, "https://esi.invalid").pathname,
  );
  if (
    !match?.[1] ||
    (error.statusCode !== 404 && error.statusCode !== 409 && error.statusCode !== 429)
  ) {
    return null;
  }
  const approvalId = decodeURIComponent(match[1]);
  if (error.statusCode === 404) {
    return { approvalId, failure: "unavailable" };
  }
  const code = errorCodeOf(error);
  if (error.statusCode === 429) {
    return code === "model_budget"
      ? { approvalId, failure: "budget" }
      : code === "user_budget"
        ? { approvalId, failure: "userBudget" }
        : null;
  }
  return {
    approvalId,
    failure:
      code === "approval_expired"
        ? "expired"
        : code === "approval_decided"
          ? "decided"
          : "unavailable",
  };
}

/** A reason a refusal may carry: the gate's, or a provider's refusal code (`web_fetch`). */
/** `file_unavailable`: a file a code run named could not be read (agent-turn D18). */
export type RefusalReason = EgressReason | WebFetchErrorCode | "file_unavailable";

const KNOWN_REASONS: readonly string[] = [
  ...EGRESS_REASONS,
  ...WEB_FETCH_ERROR_CODES,
  "file_unavailable",
];

/**
 * The reasons a refusal carries, known codes only (never anything else shown): the gate's, and
 * the codes a provider refused with (a domain the organisation excludes, unsupported content…).
 */
export function refusalOf(
  output: unknown,
): { status: "blocked" | "failed" | "refused"; reasons: RefusalReason[] } | null {
  const parsed = toolRefusalSchema.safeParse(output);
  if (!parsed.success) {
    return null;
  }
  const reasons = (parsed.data.reasons ?? []).filter((r): r is RefusalReason =>
    KNOWN_REASONS.includes(r),
  );
  return { status: parsed.data.status, reasons: [...new Set(reasons)] };
}

/** One external result as the row shows it: plain text and, at most, a safe link. */
export interface ExternalResult {
  title: string | null;
  snippet: string | null;
  href: string | null;
}

const text = (value: unknown) =>
  typeof value === "string" && value.trim() !== "" ? value.trim() : null;

/** An `http:` or `https:` URL, or null: any other scheme (`javascript:`, `data:`…) is dropped. */
export function safeHref(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  try {
    const url = new URL(value.trim());
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
}

/** The results of an external output (`{ external: true, results: [...] }`), else none. */
export function externalResultsOf(output: unknown): ExternalResult[] {
  if (!isExternalOutput(output)) {
    return [];
  }
  const results = (output as { results?: unknown }).results;
  if (!Array.isArray(results)) {
    return [];
  }
  return results.flatMap((r): ExternalResult[] => {
    if (typeof r !== "object" || r === null) {
      return [];
    }
    const o = r as Record<string, unknown>;
    const item = {
      title: text(o.title),
      snippet: text(o.snippet) ?? text(o.text) ?? text(o.content),
      href: safeHref(o.url),
    };
    return item.title || item.snippet || item.href ? [item] : [];
  });
}

/**
 * The arguments of a call as plain lines: a lone `query` is the query itself, other values read
 * `name: value` (non-strings as JSON). Never Markdown, never HTML.
 */
export function argumentLines(input: unknown): string[] {
  if (input === undefined || input === null) {
    return [];
  }
  if (typeof input !== "object" || Array.isArray(input)) {
    return [typeof input === "string" ? input : JSON.stringify(input)];
  }
  const entries = Object.entries(input as Record<string, unknown>).filter(
    ([, v]) => v !== undefined,
  );
  if (entries.length === 1 && entries[0]?.[0] === "query" && typeof entries[0][1] === "string") {
    return [entries[0][1]];
  }
  return entries.map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`);
}

/** Whether an approval is past its expiry, as the last rendered state says. */
export function approvalExpired(
  approvals: ToolApprovalsData | undefined,
  approvalId: string | undefined,
  now: number,
): boolean {
  const entry = approvalId ? approvals?.[approvalId] : undefined;
  return entry?.state === "pending" && Date.parse(entry.expiresAt) <= now;
}

/**
 * Why a new turn was refused before it started, when the server says so: the organisation's hard
 * monthly AI budget is used up (`429 model_budget`), the user's share of it is
 * (`429 user_budget`, `models.userMonthlySharePct`), or the message carries attachments while
 * they are turned off (`409 attachments_disabled`). Null for any other failure.
 */
export function turnRefusalOf(
  error: unknown,
): "model_budget" | "user_budget" | "attachments_disabled" | null {
  if (!APICallError.isInstance(error)) {
    return null;
  }
  const code = errorCodeOf(error);
  if (error.statusCode === 409) {
    return code === "attachments_disabled" ? code : null;
  }
  if (error.statusCode !== 429) {
    return null;
  }
  return code === "model_budget" || code === "user_budget" ? code : null;
}

/** The refusal's `code` from a JSON error body; "" when there is none (an unexplained failure). */
function errorCodeOf(error: APICallError): string {
  try {
    const body = JSON.parse(error.responseBody ?? "") as { code?: unknown };
    return typeof body?.code === "string" ? body.code : "";
  } catch {
    return "";
  }
}
